/**
 * TierPrivilegeMatrixScreen — Licensor-admin editor for WebApp.S_TierPrivileges.
 *
 * Restricted to msivan@myaccounts.in (or LICENSOR_CLIENT_IDS env). All other users
 * get a polite "Licensor admins only" gate.
 *
 * Features:
 *   - Industry dropdown (62 industries) + Tier tabs (Lite/Express/Standard/Pro/Enterprise)
 *   - 3-column checkbox matrix: Modules · SubModules · Screens
 *   - Cascade toggle (parent ↔ children)
 *   - "Copy from tier" shortcut
 *   - Live diff vs JSON seed (green=granted, red=revoked)
 *   - "Bulk Import Seed" — one-click ingest of all 372 JSONs
 *   - Audit fields (UpdatedBy / UpdatedAt) shown post-save
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Shield, Save, Database, RefreshCw, Copy, Loader2, AlertTriangle, CheckCircle2, Plus, Minus } from 'lucide-react';
import { API_BASE, getToken } from '@/lib/api';

const TIERS = ['Lite', 'Express', 'Standard', 'Professional', 'Enterprise'];

const fetchJSON = (path, opts = {}) =>
  fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) },
  }).then(async r => {
    const t = await r.text();
    try { return { status: r.status, body: JSON.parse(t) }; }
    catch { return { status: r.status, body: { error: t } }; }
  });

export default function TierPrivilegeMatrixScreen({ theme = 'light', onClose }) {
  const isDark = theme === 'dark';

  // ── Inject pig-priv CSS once ────────────────────────────────────────────
  useEffect(() => {
    const id = 'pig-priv-styles';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      .pig-priv-matrix{display:grid;grid-template-columns:minmax(280px,1fr) minmax(280px,1fr) minmax(280px,1fr);width:100%;min-width:0;height:100%;}
      .pig-priv-panel{min-width:0;overflow:hidden;border-right:1px solid #e5e7eb;display:flex;flex-direction:column;}
      .pig-priv-panel-body{width:100%;min-width:0;overflow-y:auto;overflow-x:hidden;flex:1;}
      .pig-priv-row{display:grid;grid-template-columns:32px minmax(0,1fr);align-items:center;min-height:36px;padding:0 12px;box-sizing:border-box;}
      .pig-priv-row input[type="checkbox"]{appearance:auto;-webkit-appearance:checkbox;position:static;inset:auto;transform:none;width:16px;height:16px;margin:0 auto;cursor:pointer;}
      .pig-priv-row span,.pig-priv-label{min-width:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;}
    `;
    document.head.appendChild(s);
  }, []);

  // ─── state ────────────────────────────────────────────────────────────────
  const [accessDenied, setAccessDenied] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [catalog, setCatalog]       = useState({ modules: [], submodules: [], screens: [] });
  const [industry, setIndustry]     = useState('');
  const [tier, setTier]             = useState('Lite');
  const [bundle, setBundle]         = useState(null); // {dbBundle, seedBundle, diff}
  const [edits, setEdits]           = useState({ modules: new Set(), subModules: new Set(), screens: new Set(), templateDB: '' });
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [seeding, setSeeding]       = useState(false);
  const [msg, setMsg]               = useState(null);

  // ─── data loads ───────────────────────────────────────────────────────────
  const loadIndustries = useCallback(async () => {
    const r = await fetchJSON('/api/license/industries');
    setIndustries(r.body?.industries || []);
    if (!industry && r.body?.industries?.length) setIndustry(r.body.industries[0].code);
  }, [industry]);

  const loadCatalog = useCallback(async () => {
    const r = await fetchJSON('/api/admin/tier-privileges/menu/catalog');
    if (r.status === 403) { setAccessDenied(true); return; }
    if (r.status === 200) setCatalog(r.body);
  }, []);

  const loadBundle = useCallback(async () => {
    if (!industry || !tier) return;
    setLoading(true); setMsg(null);
    const r = await fetchJSON(`/api/admin/tier-privileges/${encodeURIComponent(industry)}/${encodeURIComponent(tier)}`);
    setLoading(false);
    if (r.status === 403) { setAccessDenied(true); return; }
    if (r.status !== 200) { setMsg({ kind: 'error', text: r.body?.error || 'Failed to load bundle' }); return; }
    setBundle(r.body);
    // Pre-seed edits from db OR seed bundle
    const src = r.body.dbBundle || r.body.seedBundle || { modules: [], subModules: [], screens: [], templateDB: '' };
    setEdits({
      modules:    new Set(src.modules    || []),
      subModules: new Set(src.subModules || []),
      screens:    new Set(src.screens    || []),
      templateDB: src.templateDB || '',
    });
  }, [industry, tier]);

  useEffect(() => { loadIndustries(); loadCatalog(); }, [loadIndustries, loadCatalog]);
  useEffect(() => { loadBundle(); }, [loadBundle]);

  // ─── cascade helpers ───────────────────────────────────────────────────────
  const submodulesByModule = useMemo(() => {
    const m = {};
    (catalog.submodules || []).forEach(s => {
      const parent = String(s.PARENT_MODULE_ID || '');
      if (!m[parent]) m[parent] = [];
      m[parent].push(s);
    });
    return m;
  }, [catalog.submodules]);

  const screensBySubModule = useMemo(() => {
    const m = {};
    (catalog.screens || []).forEach(s => {
      const sid = String(s.SCREEN_ID || '');
      const parts = sid.split('.');
      const subModId = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : sid;
      if (!m[subModId]) m[subModId] = [];
      m[subModId].push(s);
    });
    return m;
  }, [catalog.screens]);

  const toggleSet = (key, id) => {
    setEdits(prev => {
      const next = new Set(prev[key]);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, [key]: next };
    });
  };

  const toggleModuleCascade = (moduleId, checked) => {
    setEdits(prev => {
      const modules    = new Set(prev.modules);
      const subModules = new Set(prev.subModules);
      const screens    = new Set(prev.screens);
      const mid = String(moduleId);
      if (checked) modules.add(mid); else modules.delete(mid);
      const subs = submodulesByModule[mid] || [];
      subs.forEach(s => {
        const sid = String(s.MODULE_ID);
        if (checked) subModules.add(sid); else subModules.delete(sid);
        const scrs = screensBySubModule[sid] || [];
        scrs.forEach(sc => {
          const scid = String(sc.SCREEN_ID);
          if (checked) screens.add(scid); else screens.delete(scid);
        });
      });
      return { ...prev, modules, subModules, screens };
    });
  };

  const toggleSubModuleCascade = (subModId, parentModId, checked) => {
    setEdits(prev => {
      const modules    = new Set(prev.modules);
      const subModules = new Set(prev.subModules);
      const screens    = new Set(prev.screens);
      const smid = String(subModId);
      if (checked) { subModules.add(smid); if (parentModId) modules.add(String(parentModId)); }
      else subModules.delete(smid);
      const scrs = screensBySubModule[smid] || [];
      scrs.forEach(sc => {
        const scid = String(sc.SCREEN_ID);
        if (checked) screens.add(scid); else screens.delete(scid);
      });
      return { ...prev, modules, subModules, screens };
    });
  };

  // ─── save / actions ────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true); setMsg(null);
    const r = await fetchJSON('/api/admin/tier-privileges', {
      method: 'POST',
      body: JSON.stringify({
        industry, tier,
        modules:    Array.from(edits.modules),
        subModules: Array.from(edits.subModules),
        screens:    Array.from(edits.screens),
        templateDB: edits.templateDB || null,
      }),
    });
    setSaving(false);
    if (r.status === 200 && r.body?.success) {
      setMsg({ kind: 'ok', text: `Saved ${industry} · ${tier} — by ${r.body.updatedBy}` });
      loadBundle();
    } else {
      setMsg({ kind: 'error', text: r.body?.error || 'Save failed' });
    }
  };

  const bulkSeed = async () => {
    if (!window.confirm('Import ALL 372 seed bundles into MSSQL? Existing rows will be SKIPPED (use Overwrite to replace).')) return;
    setSeeding(true); setMsg(null);
    const r = await fetchJSON('/api/admin/tier-privileges/bulk-import-seed', {
      method: 'POST',
      body: JSON.stringify({ overwrite: false }),
    });
    if (r.status !== 202 && r.status !== 200) {
      setSeeding(false);
      setMsg({ kind: 'error', text: r.body?.error || 'Seed import failed to start' });
      return;
    }
    const jobId = r.body.jobId;
    if (!jobId) {
      // Old synchronous response path (fallback)
      setSeeding(false);
      setMsg({ kind: 'ok', text: `Imported: ${r.body.imported}, skipped: ${r.body.skipped}, failed: ${r.body.failed}` });
      return;
    }
    setMsg({ kind: 'ok', text: `Import started (job ${jobId.slice(-8)}) — polling…` });
    // Poll every 2s up to ~3 min
    const poll = async (tries = 0) => {
      const p = await fetchJSON(`/api/admin/tier-privileges/bulk-import-seed/${jobId}`);
      if (p.status === 200 && p.body) {
        const { status, imported, skipped, failed, totalCombos } = p.body;
        if (status === 'done') {
          setSeeding(false);
          setMsg({ kind: 'ok', text: `Seed import complete — imported: ${imported}, skipped: ${skipped}, failed: ${failed} of ${totalCombos}` });
          return;
        }
        if (status === 'failed') {
          setSeeding(false);
          setMsg({ kind: 'error', text: `Import failed: ${p.body.error || 'unknown'}` });
          return;
        }
        setMsg({ kind: 'ok', text: `Importing… ${imported + skipped + failed}/${totalCombos}` });
      }
      if (tries < 90) setTimeout(() => poll(tries + 1), 2000);
      else { setSeeding(false); setMsg({ kind: 'error', text: 'Polling timed out. Job may still be running — refresh later.' }); }
    };
    poll();
  };

  const copyFromTier = async () => {
    const src = window.prompt(`Copy from which tier? (${TIERS.filter(t => t !== tier).join(', ')})`);
    if (!src || src === tier) return;
    const r = await fetchJSON(`/api/admin/tier-privileges/${encodeURIComponent(industry)}/${encodeURIComponent(src)}`);
    if (r.status === 200 && r.body?.dbBundle) {
      const src_bundle = r.body.dbBundle;
      setEdits({
        modules:    new Set(src_bundle.modules),
        subModules: new Set(src_bundle.subModules),
        screens:    new Set(src_bundle.screens),
        templateDB: src_bundle.templateDB || edits.templateDB,
      });
      setMsg({ kind: 'ok', text: `Copied privileges from ${src} (unsaved — click Save)` });
    } else {
      setMsg({ kind: 'error', text: `No DB bundle for ${src} to copy from. Try saving ${src} first.` });
    }
  };

  // ─── render ────────────────────────────────────────────────────────────────
  if (accessDenied) {
    return (
      <div data-testid="tier-priv-access-denied" className={`h-full flex items-center justify-center ${isDark ? 'bg-[#0a0a0a] text-white' : 'bg-slate-50 text-slate-900'}`}>
        <div className="text-center max-w-sm">
          <Shield size={40} className="mx-auto text-amber-500 mb-3" />
          <h2 className="text-lg font-bold mb-2">Licensor Admins Only</h2>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            The Tier-Privilege Matrix editor is restricted to the RetailCLIQ licensor team
            (e.g. <code>msivan@myaccounts.in</code>). Contact licensor support to gain access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="tier-privilege-matrix-screen" className={`h-full flex flex-col ${isDark ? 'bg-[#0a0a0a] text-white' : 'bg-white text-slate-900'}`}>
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className={`px-5 py-3 border-b flex items-center gap-3 flex-shrink-0 ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
        <Shield size={18} className="text-blue-500" />
        <div className="flex-1">
          <h1 className="text-sm font-bold">Tier-Privilege Matrix</h1>
          <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Edit module / submodule / screen access by industry × tier — written to WEBAPP.S_TierPrivileges</p>
        </div>
        <button
          onClick={bulkSeed}
          disabled={seeding}
          data-testid="bulk-seed-import-btn"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors ${isDark ? 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
          {seeding ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
          Bulk Import Seed
        </button>
        <button
          onClick={copyFromTier}
          data-testid="copy-from-tier-btn"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors ${isDark ? 'bg-purple-500/15 text-purple-400 hover:bg-purple-500/25' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}>
          <Copy size={12} /> Copy From Tier
        </button>
        <button
          onClick={save}
          disabled={saving || !industry || !tier}
          data-testid="tier-priv-save-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
      </div>

      {/* ── Controls + status banner ──────────────────────────────────────── */}
      <div className={`px-5 py-2.5 flex items-center gap-3 border-b flex-shrink-0 ${isDark ? 'border-[#222] bg-[#0e0e0e]' : 'border-slate-100 bg-slate-50'}`}>
        <label className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Industry</label>
        <select
          data-testid="industry-select"
          value={industry}
          onChange={e => setIndustry(e.target.value)}
          className={`px-2 py-1 rounded text-xs border outline-none ${isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
          {industries.map(i => <option key={i.code} value={i.code}>{i.name || i.code}</option>)}
        </select>
        <label className={`text-[10px] font-bold uppercase tracking-wider ml-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Tier</label>
        <div className={`flex p-0.5 rounded-md ${isDark ? 'bg-[#151515]' : 'bg-white border border-slate-200'}`}>
          {TIERS.map(t => (
            <button key={t} onClick={() => setTier(t)} data-testid={`tier-tab-${t}`}
              className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors ${tier === t ? 'bg-blue-600 text-white' : isDark ? 'text-slate-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
              {t}
            </button>
          ))}
        </div>
        <label className={`text-[10px] font-bold uppercase tracking-wider ml-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Template DB</label>
        <input
          data-testid="template-db-input"
          type="text"
          value={edits.templateDB}
          onChange={e => setEdits(p => ({ ...p, templateDB: e.target.value }))}
          placeholder="e.g. RC26ERP_APPAREL_TEMPLATE"
          className={`px-2 py-1 rounded text-xs border outline-none flex-1 max-w-xs ${isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-slate-200 text-slate-900'}`} />

        {/* Source + audit info */}
        <div className="flex-1" />
        {bundle && (
          <div className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
            <span className={`px-1.5 py-0.5 rounded mr-2 text-[9px] font-bold uppercase ${bundle.source === 'mssql' ? 'bg-green-500/15 text-green-500' : bundle.source === 'seed' ? 'bg-amber-500/15 text-amber-500' : 'bg-slate-500/15 text-slate-500'}`}>
              {bundle.source}
            </span>
            {bundle.dbBundle?.updatedBy && <>by {bundle.dbBundle.updatedBy} · {new Date(bundle.dbBundle.updatedAt).toLocaleString('en-IN')}</>}
          </div>
        )}
      </div>

      {msg && (
        <div className={`px-5 py-2 text-[11px] flex items-center gap-2 border-b flex-shrink-0 ${msg.kind === 'ok' ? (isDark ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-green-50 text-green-700 border-green-200') : (isDark ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-red-50 text-red-700 border-red-200')}`}>
          {msg.kind === 'ok' ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {msg.text}
        </div>
      )}

      {/* ── Matrix ────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center"><RefreshCw size={24} className="animate-spin text-blue-500" /></div>
      ) : (
        <div className="pig-priv-matrix flex-1" style={{ minHeight: 0, overflow: 'hidden' }}>
          {/* Column 1: Modules */}
          <Column
            title="Modules"
            count={edits.modules.size}
            total={catalog.modules?.length}
            theme={theme}
            items={(catalog.modules || []).map(m => ({
              id: String(m.MODULE_ID), label: m.MODULE_NAME,
              checked: edits.modules.has(String(m.MODULE_ID)),
            }))}
            onToggle={(id, checked) => toggleModuleCascade(id, checked)}
            testid="col-modules"
          />
          {/* Column 2: SubModules */}
          <Column
            title="SubModules"
            count={edits.subModules.size}
            total={(catalog.submodules || []).length}
            theme={theme}
            items={(catalog.submodules || []).map(s => ({
              id: String(s.MODULE_ID),
              label: s.MODULE_NAME,
              parentId: String(s.PARENT_MODULE_ID || ''),
              checked: edits.subModules.has(String(s.MODULE_ID)),
            }))}
            onToggle={(id, checked, parentId) => toggleSubModuleCascade(id, parentId, checked)}
            testid="col-submodules"
          />
          {/* Column 3: Screens */}
          <Column
            title="Screens"
            count={edits.screens.size}
            total={(catalog.screens || []).length}
            theme={theme}
            items={(catalog.screens || []).map(s => ({
              id: String(s.SCREEN_ID), label: s.SCREEN_TITLE,
              checked: edits.screens.has(String(s.SCREEN_ID)),
            }))}
            onToggle={(id) => toggleSet('screens', id)}
            testid="col-screens"
          />
        </div>
      )}

      {/* ── Diff vs Seed footer ──────────────────────────────────────────── */}
      {bundle?.diff && (
        <div className={`flex-shrink-0 px-5 py-2 border-t flex items-center gap-4 text-[10px] ${isDark ? 'border-[#222] bg-[#0e0e0e]' : 'border-slate-200 bg-slate-50'}`}>
          <span className={`font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>vs Seed:</span>
          <DiffBadge label="modules granted" count={bundle.diff.modulesGranted.length} color="green" theme={theme} />
          <DiffBadge label="modules revoked" count={bundle.diff.modulesRevoked.length} color="red" theme={theme} />
          <DiffBadge label="screens granted" count={bundle.diff.screensGranted.length} color="green" theme={theme} />
          <DiffBadge label="screens revoked" count={bundle.diff.screensRevoked.length} color="red" theme={theme} />
        </div>
      )}
    </div>
  );
}

function Column({ title, items, onToggle, count, total, testid }) {
  const [filter, setFilter] = useState('');
  const filtered = useMemo(
    () => items.filter(i => !filter || (i.label || '').toLowerCase().includes(filter.toLowerCase()) || String(i.id).includes(filter)),
    [items, filter]
  );
  return (
    <div className="pig-priv-panel" data-testid={testid}>
      {/* Header */}
      <div style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569' }}>{title}</span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>{count} / {total || 0}</span>
        <input
          value={filter} onChange={e => setFilter(e.target.value)}
          placeholder="filter..."
          style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 4, fontSize: 10, border: '1px solid #e2e8f0', outline: 'none', width: 90, background: '#fff', color: '#1e293b' }}
        />
      </div>
      {/* Scrollable rows */}
      <div className="pig-priv-panel-body" style={{ flex: 1, height: 0, minHeight: 0 }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '24px 12px', textAlign: 'center', fontSize: 10, color: '#94a3b8' }}>No items</div>
        ) : filtered.map(item => (
          <label key={item.id} className="pig-priv-row" data-testid={`row-${testid}-${item.id}`}
            style={{ cursor: 'pointer', background: 'transparent' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <input
              type="checkbox"
              checked={item.checked}
              onChange={e => onToggle(item.id, e.target.checked, item.parentId)}
            />
            <span className="pig-priv-label" style={{ fontSize: 11, color: '#334155', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontWeight: 500 }}>{item.label || '(unnamed)'}</span>
              <span style={{ fontSize: 9, color: '#94a3b8', fontFamily: 'monospace', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{item.id}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function DiffBadge({ label, count, color, theme }) {
  const isDark = theme === 'dark';
  if (count === 0) return null;
  const isGreen = color === 'green';
  return (
    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
      isGreen
        ? (isDark ? 'bg-green-500/15 text-green-400' : 'bg-green-50 text-green-700')
        : (isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-50 text-red-700')
    }`}>
      {isGreen ? <Plus size={9} /> : <Minus size={9} />} {count} {label}
    </span>
  );
}
